import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async (e, p) => {
    setLoading(true);
    try {
      await login(e, p);
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0B0F19] flex items-center justify-center px-4">
      <div className="blob absolute -top-24 -left-24 h-96 w-96 rounded-full bg-violet-600/30 blur-3xl" />
      <div className="blob absolute -bottom-32 -right-24 h-[28rem] w-[28rem] rounded-full bg-pink-600/25 blur-3xl" style={{ animationDelay: "-4s" }} />
      <div className="blob absolute top-1/3 right-1/4 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" style={{ animationDelay: "-2s" }} />
      <div className="absolute inset-0 canvas-dots opacity-40" />

      <div className="relative w-full max-w-md card-pop">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 text-5xl font-extrabold tracking-tight text-white font-display">
            <Sparkles className="h-10 w-10 text-violet-400" />
            Nexfy
          </div>
          <p className="mt-2 font-hand text-2xl text-pink-300">your workboard, your vibes ✦</p>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }}
          className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl shadow-2xl"
        >
          <label className="mb-1 block text-xs font-mono uppercase tracking-wider text-slate-400">Email</label>
          <input
            data-testid="login-email-input"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@nexfy.in"
            className="mb-4 w-full rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-white placeholder-slate-500 outline-none focus:border-violet-400 transition-colors"
          />
          <label className="mb-1 block text-xs font-mono uppercase tracking-wider text-slate-400">Password</label>
          <input
            data-testid="login-password-input"
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="mb-2 w-full rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-white placeholder-slate-500 outline-none focus:border-violet-400 transition-colors"
          />
          <div className="mb-5 text-right">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-violet-300 hover:text-violet-200">
              Forgot password?
            </Link>
          </div>
          <button
            data-testid="login-submit-button"
            type="submit" disabled={loading}
            className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 py-3 font-bold text-white shadow-lg shadow-violet-500/30 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
          >
            {loading ? "Beaming in..." : "Enter Nexfy →"}
          </button>
          <p className="mt-4 text-center text-sm text-slate-400">
            New here? <Link to="/signup" data-testid="login-signup-link" className="text-violet-300 hover:text-violet-200">Create your board</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
